import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PackageOpen } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PageActionBar } from "@/components/layout/PageActionBar";
import { Product } from "../types";
import BulkActionModal from "./BulkActionModal";
import BulkStocktakeUploadModal from "./BulkStocktakeUploadModal";
import BulkThresholdUploadModal from "./BulkThresholdUploadModal";
import IndividualStocktakeModal from "./IndividualStocktakeModal";
import UnavailableAdjustmentModal from "./UnavailableAdjustmentModal";
import AddPrePrintedStockModal from "./AddPrePrintedStockModal";
import { useStocktakeExport } from "../hooks/useStocktakeExport";
import { countSheetSize } from "../utils/stocktakeCsv";
import { useStockAge } from "@/features/reporting/hooks/useStockAge";
import { useInventorySkuVelocity } from "@/features/reporting/hooks/useReportingV2";
import { useLogger } from "@/lib/logger";
import { InventoryTableFilters } from "./InventoryTableFilters";
import { InventoryTableMatrix } from "./InventoryTableMatrix";
import { InventoryTableList } from "./InventoryTableList";
import InventoryProductDetailModal from "./InventoryProductDetailModal";
import { InventoryBulkSelectionBar } from "./InventoryBulkSelectionBar";
import { BulkStockEditorDialog } from "./BulkStockEditorDialog";
import { isLowStock, isOutOfStock, stockSortValue } from "../utils/stockUtils";
import { ClientFilterDropdown } from "./ClientFilterDropdown";
import { ALL_CLIENTS, useClientOrgFilter } from "../hooks/useClientOrgFilter";
import { useHeaderLeftSlot } from "@/contexts/HeaderSlotContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useLocation } from "@/contexts/LocationContext";
import { WHO_ANY, type WhoCanUseFilter as WhoFilter } from "@/features/base-garments/lib/base-garments-url";

interface InventoryTableProps {
  products: Product[];
  onAddStock: () => void;
  // BLU-3xx Phase 47.01.2-B-04: extended signature. `targetOrganizationId`
  // is the cross-org row owner chosen in the Stocktake modal at shared 3PL
  // locations.
  // BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved Product (not sku).
  onSetStock?: (
    product: Product,
    quantity: number,
    locationId?: string,
    targetOrganizationId?: string,
    /** Optional operator reference / order number (Thread Lab stock log). */
    reference?: string,
  ) => Promise<boolean> | boolean;
  loading?: boolean;
  /**
   * BLU-1189: inventory actions are supplied by the page. This component used
   * to pull them off its own `useInventory()` instance, which mounted a SECOND
   * inventory query (a second full-catalogue fetch whenever the page's type
   * filter made the keys differ) on top of the page's.
   */
  downloadStockCsv: (items?: Product[]) => Promise<void>;
  moveToUnavailable: MoveStockAction;
  moveToAvailable: MoveStockAction;
  onProductUpdated: () => void;
  onAddStockIndividual?: () => void;
  onAddStockCsv?: () => void;
  onReconcile?: () => void;
  stockActionsDisabled?: boolean;
  /** BLU-711 A1: pre-print stays owner-only even when stock writes are granted. */
  prePrintActionsDisabled?: boolean;
  /**
   * BLU-717 S3: why pre-printing is unavailable, when it is NOT the 3PL reason
   * (the filter bar owns that copy as its default). Typically "not enabled at
   * this location".
   */
  prePrintActionsDisabledReason?: string;
  /** BLU-3xx Phase 47.01.2-B-04: optional reason copy for the warning. */
  stockActionsDisabledReason?: string;
  /**
   * ace/trims-ux: server-side type filter (POD / PAP / Trims). Owned by the
   * page (drives the actual data fetch), threaded through here purely to
   * render the toolbar control.
   */
  productionTypeFilter?: 'PAP' | 'POD' | 'TRIM';
  onProductionTypeFilterChange?: (value: 'PAP' | 'POD' | 'TRIM' | undefined) => void;
  /**
   * BLU-1394 F25: "Who can use it" for blanks (3PL orgs). Owned by the page
   * (a query input, like the type filter); absent = not offered.
   */
  whoFilter?: WhoFilter;
  onWhoFilterChange?: (value: WhoFilter) => void;
  /** The org's clients across its own floors: the filter list and the "Only <client>" badge names. */
  whoClients?: ReadonlyArray<{ id: string; name: string }>;
}

type MoveStockAction = (
  product: Product,
  quantity: number,
  reason?: string,
  targetOrganizationId?: string | null,
) => Promise<boolean>;

type SortField = 'sku' | 'type' | 'color' | 'size' | 'stockLevel' | 'velocity';
type SortDirection = 'asc' | 'desc';
type Filters = {
  type: string[];
  color: string[];
  size: string[];
};

const InventoryTable: React.FC<InventoryTableProps> = ({
  products,
  onAddStock,
  onSetStock,
  loading = false,
  downloadStockCsv,
  moveToUnavailable,
  moveToAvailable,
  onProductUpdated,
  onAddStockIndividual,
  onAddStockCsv,
  onReconcile,
  stockActionsDisabled = false,
  prePrintActionsDisabled = false,
  prePrintActionsDisabledReason,
  stockActionsDisabledReason,
  productionTypeFilter,
  onProductionTypeFilterChange,
  whoFilter = WHO_ANY,
  onWhoFilterChange,
  whoClients,
}) => {
  const { exportRows, exportExample, includeCurrentCounts, setIncludeCurrentCounts } = useStocktakeExport();
  const logger = useLogger();
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const { t } = useTranslation('inventory');
  const ownOrgId = organization?.id;

  // BLU-3xx Phase 47.01.2-B-03: cross-org per-client filter. Mirrors the
  // BLU-389 OrdersPage pattern. Visibility is gated by the feature flag AND
  // by the presence of multi-org rows in the current data set, so single-
  // tenant deployments and single-org locations see no UI change.
  const clientFilter = useClientOrgFilter<Product>({
    rows: products,
    ownOrgId: organization?.id,
    ownOrgName: organization?.name,
    locationId: selectedLocation?.id,
  });
  // F25: the name on a blank's "Only <client>" badge.
  const reservedClientName = useCallback(
    (id: string) => whoClients?.find((client) => client.id === id)?.name ?? null,
    [whoClients],
  );

  // The downstream search/sort/pagination chain runs on the client-filtered
  // subset. When the filter is "All clients" (the default) this is a no-op
  // pass-through, preserving byte-equivalent single-tenant behaviour.
  const visibleProducts = clientFilter.filteredRows;

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<Filters>({
    type: [],
    color: [],
    size: []
  });
  const [stockFilter, setStockFilter] = useState<'low' | 'out' | 'key' | 'preprinted' | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);

  // View mode state
  const [viewMode, setViewMode] = useState<'list' | 'table'>('list');
  const [showStockAge, setShowStockAge] = useState(false);

  // Velocity uses the same net-fulfilment ledger as the full Consumption
  // report, through a current-window-only endpoint sized for this operational
  // table. The full report's prior-period/order-count enrichment times out at
  // high-volume locations such as Threadheads HQ and is intentionally kept off
  // this screen.
  // Keep the window stable for the lifetime of this screen so React Query's
  // key does not drift on every render. The query remains org-scoped on the
  // server; partner-owned rows therefore display an explicit unavailable
  // state rather than borrowing another tenant's consumption.
  const velocityPeriod = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
    return { startDate, endDate };
  }, []);
  const velocityQuery = useInventorySkuVelocity({
    organizationId: ownOrgId ?? null,
    startDate: velocityPeriod.startDate,
    endDate: velocityPeriod.endDate,
    locationId: selectedLocation?.id ?? null,
    enabled: viewMode === 'list',
  });
  const velocityBySkuId = useMemo(() => {
    const map = new Map<string, { velocity: number; units: number }>();
    for (const row of velocityQuery.data?.rows ?? []) {
      map.set(row.skuId, {
        velocity: Math.max(0, row.velocity),
        units: row.units,
      });
    }
    return map;
  }, [velocityQuery.data?.rows]);
  const getProductVelocity = useCallback((product: Product): number | null => {
    if (
      (product.ownerOrganizationId && ownOrgId && product.ownerOrganizationId !== ownOrgId) ||
      velocityQuery.isError ||
      velocityQuery.isLoading
    ) {
      return null;
    }
    return velocityBySkuId.get(product.id)?.velocity ?? 0;
  }, [ownOrgId, velocityBySkuId, velocityQuery.isError, velocityQuery.isLoading]);

  // The Stock Age toggle button only renders in matrix (table) view. If the
  // user enables it then switches to list view, there's no UI to turn it off
  // — so reset it whenever we leave matrix view.
  useEffect(() => {
    if (viewMode !== 'table' && showStockAge) {
      setShowStockAge(false);
    }
  }, [viewMode, showStockAge]);

  // Sorting state — default to available quantity descending
  const [sortField, setSortField] = useState<SortField>('stockLevel');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Always-on multi-select
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  // Anchor for shift-click range selection. Holds the id of the last row the
  // operator toggled; a shift-click then applies the ANCHOR's state to every
  // row between the two (Gmail semantics) so an unselecting shift-click
  // clears a range instead of filling it. Not reused from useVariantSelection
  // / useOrderSelection: those hooks own their own selection Set and prune it
  // to the visible item array (which would wipe this table's cross-page
  // selection), and they only ADD on shift-click rather than applying the
  // anchor's state.
  const selectionAnchorRef = React.useRef<string | null>(null);

  // Modal state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState<"stock" | "image" | "delete" | null>(null);
  const [isIndividualStocktakeModalOpen, setIsIndividualStocktakeModalOpen] = useState(false);
  const [isBulkStocktakeUploadOpen, setIsBulkStocktakeUploadOpen] = useState(false);
  const [isBulkThresholdUploadOpen, setIsBulkThresholdUploadOpen] = useState(false);
  const [presetStocktakeSku, setPresetStocktakeSku] = useState<string | null>(null);
  const [isUnavailableModalOpen, setIsUnavailableModalOpen] = useState(false);
  const [unavailableMode, setUnavailableMode] = useState<'to_unavailable' | 'to_available'>('to_unavailable');
  const [isPrePrintedStockModalOpen, setIsPrePrintedStockModalOpen] = useState(false);
  const [isBulkStockEditorOpen, setIsBulkStockEditorOpen] = useState(false);
  const [selectedProductForDetails, setSelectedProductForDetails] = useState<Product | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Fetch stock age data when enabled
  const { stockAgeData, loading: stockAgeLoading } = useStockAge(showStockAge && viewMode === 'table');

  // Create a map for quick lookup of stock age data
  const stockAgeMap = useMemo(() => {
    const map = new Map<string, typeof stockAgeData[number]>();
    stockAgeData.forEach((item: typeof stockAgeData[number]) => {
      map.set(item.sku_id, item);
    });
    return map;
  }, [stockAgeData]);

  // Memoize unique values extraction for filter dropdowns
  const uniqueTypes = useMemo(() => {
    return Array.from(new Set(products.filter(p => p.category.type).map(p => p.category.type))).sort() as string[];
  }, [products]);

  const uniqueColors = useMemo(() => {
    return Array.from(new Set(products.filter(p => p.category.color).map(p => p.category.color))).sort() as string[];
  }, [products]);

  const uniqueSizes = useMemo(() => {
    const sizeOrder = ["XS", "S", "M", "L", "XL", "2X", "3X", "4X", "4", "6", "8", "10", "12", "14", "16"];
    const sizes = Array.from(new Set(products.filter(p => p.category.size).map(p => p.category.size))) as string[];

    return sizes.sort((a, b) => {
      const indexA = sizeOrder.indexOf(a);
      const indexB = sizeOrder.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [products]);

  // Set filter active state when any filter is applied
  useEffect(() => {
    const hasActiveFilter =
      filters.type.length > 0 ||
      filters.color.length > 0 ||
      filters.size.length > 0 ||
      stockFilter !== null ||
      productionTypeFilter !== undefined ||
      whoFilter.kind !== 'any';
    setIsFilterActive(hasActiveFilter);
  }, [filters, productionTypeFilter, stockFilter, whoFilter]);

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      type: [],
      color: [],
      size: []
    });
    setStockFilter(null);
    onProductionTypeFilterChange?.(undefined);
    onWhoFilterChange?.(WHO_ANY);
  };

  // Smart search function that handles multiple terms
  const smartSearch = (product: Product, searchTerms: string[]): boolean => {
    if (searchTerms.length === 0) return true;

    const searchableFields = [
      product.sku,
      product.title || '',
      product.category.type || '',
      product.category.color || '',
      product.category.size || '',
    ].join(' ').toLowerCase();

    return searchTerms.every(term =>
      searchableFields.includes(term.toLowerCase())
    );
  };

  // Memoize expensive filtering and sorting operations
  const lastFilterKeyRef = React.useRef<string>('');
  const filteredProducts = useMemo(() => {
    const key = `${visibleProducts.length}|${searchTerm}|${filters.type.join(',')}|${filters.color.join(',')}|${filters.size.join(',')}|${stockFilter}|${sortField}|${sortDirection}|${viewMode}`;
    if (process.env.NODE_ENV === 'development' && lastFilterKeyRef.current !== key) {
      lastFilterKeyRef.current = key;
      logger.debug('Recalculating filtered products', {
        operation: 'filter_products',
        viewMode,
        productCount: visibleProducts.length
      });
    }

    return visibleProducts.filter(product => {
      const searchTerms = searchTerm.trim().split(/\s+/).filter(term => term.length > 0);
      const matchesSearch = smartSearch(product, searchTerms);
      const matchesType = filters.type.length === 0 || (product.category.type && filters.type.includes(product.category.type));
      const matchesColor = filters.color.length === 0 || (product.category.color && filters.color.includes(product.category.color));
      const matchesSize = filters.size.length === 0 || (product.category.size && filters.size.includes(product.category.size));

      // BLU-1426: out and low stock read a client's alias row by the 3PL's
      // shared pool, the figure its stock cell shows, never its own zero.
      let matchesStockFilter = true;
      if (stockFilter === 'out') {
        matchesStockFilter = isOutOfStock(product);
      } else if (stockFilter === 'low') {
        matchesStockFilter = isLowStock(product);
      } else if (stockFilter === 'key') {
        matchesStockFilter = Boolean(product.isKeyItem);
      } else if (stockFilter === 'preprinted') {
        matchesStockFilter = Boolean(product.isPreprinted);
      }

      return matchesSearch && matchesType && matchesColor && matchesSize && matchesStockFilter;
    }).sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;

      if (sortField === 'stockLevel') {
        // BLU-1426: an alias row sorts by the shared pool it shows.
        return dir * (stockSortValue(a) - stockSortValue(b));
      }

      if (sortField === 'velocity') {
        const velocityA = getProductVelocity(a);
        const velocityB = getProductVelocity(b);
        // Unknown partner/error/loading rows always sink below known values;
        // reversing the sort should not bring an unknown to the top.
        if (velocityA === null && velocityB === null) return a.sku.localeCompare(b.sku);
        if (velocityA === null) return 1;
        if (velocityB === null) return -1;
        if (velocityA !== velocityB) return dir * (velocityA - velocityB);
        return a.sku.localeCompare(b.sku);
      }

      if (sortField === 'size') {
        // Apparel size ordering (XS → 4XL) instead of alphabetical
        const sizeRank: Record<string, number> = {
          'XXS': 1, '2XS': 1, 'XS': 2, 'S': 3, 'M': 4, 'L': 5, 'XL': 6,
          'XXL': 7, '2XL': 7, '2X': 7, '3XL': 8, '3X': 8, '4XL': 9, '4X': 9,
          '5XL': 10, '5X': 10,
        };
        const rankA = sizeRank[(a.category.size || '').toUpperCase()] ?? 99;
        const rankB = sizeRank[(b.category.size || '').toUpperCase()] ?? 99;
        if (rankA !== rankB) return dir * (rankA - rankB);
        // Secondary sort by SKU for stable ordering within same size
        return a.sku.localeCompare(b.sku);
      }

      let valueA: string;
      let valueB: string;

      if (sortField === 'sku') {
        valueA = a.sku;
        valueB = b.sku;
      } else if (sortField === 'type') {
        valueA = a.category.type || '';
        valueB = b.category.type || '';
      } else {
        valueA = a.category.color || '';
        valueB = b.category.color || '';
      }

      if (valueA < valueB) return -dir;
      if (valueA > valueB) return dir;
      return 0;
    });
  }, [visibleProducts, searchTerm, filters, stockFilter, sortField, sortDirection, viewMode, logger, getProductVelocity]);

  const maxVelocity = useMemo(
    () => filteredProducts.reduce((max, product) => {
      const velocity = getProductVelocity(product);
      return velocity === null ? max : Math.max(max, velocity);
    }, 0),
    [filteredProducts, getProductVelocity],
  );

  // BLU-3xx stocktake count sheet: rows selected on the CURRENT page must
  // still respect the cross-org client filter and the search/facet filters —
  // `filteredProducts` (not the raw `products` prop) is the source of truth.
  const stocktakeSelectedRows = useMemo(
    () => filteredProducts.filter(p => selectedProducts.has(p.id)),
    [filteredProducts, selectedProducts]
  );
  // What each count sheet export writes (Just in time pairs and aliases out,
  // shared pairs as one line): the menu labels state the file, not the rows.
  const countSheetAll = useMemo(() => countSheetSize(filteredProducts), [filteredProducts]);
  const countSheetSelected = useMemo(() => countSheetSize(stocktakeSelectedRows), [stocktakeSelectedRows]);

  // Pagination calculation
  const displayProducts = filteredProducts;

  const totalItems = displayProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return displayProducts.slice(startIndex, endIndex);
  }, [displayProducts, currentPage, itemsPerPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, stockFilter, clientFilter.clientOrgFilter, productionTypeFilter, whoFilter]);

  // Preserve selections when switching views
  useEffect(() => {}, [viewMode]);

  const handleCheckboxChange = useCallback((
    productId: string,
    event?: { shiftKey?: boolean },
  ) => {
    const anchorId = selectionAnchorRef.current;
    const useRange = Boolean(event?.shiftKey) && !!anchorId && anchorId !== productId;

    setSelectedProducts(prev => {
      const next = new Set(prev);

      if (useRange && anchorId) {
        // Range runs over the CURRENT page ordering — what the operator can
        // actually see between the two clicks.
        const pageIds = paginatedProducts.map(p => p.id);
        const from = pageIds.indexOf(anchorId);
        const to = pageIds.indexOf(productId);
        if (from !== -1 && to !== -1) {
          const shouldSelect = prev.has(anchorId);
          const rangeIds = pageIds.slice(Math.min(from, to), Math.max(from, to) + 1);
          rangeIds.forEach((id) => {
            if (shouldSelect) next.add(id);
            else next.delete(id);
          });
          return next;
        }
        // Anchor scrolled off this page (paged or re-filtered) — fall through
        // to a plain toggle rather than guessing at a range.
      }

      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });

    selectionAnchorRef.current = productId;
  }, [paginatedProducts]);

  const handleSelectAll = useCallback(() => {
    const currentPageIds = paginatedProducts.map(p => p.id);
    const allCurrentPageSelected = currentPageIds.every(id => selectedProducts.has(id));

    if (allCurrentPageSelected) {
      const newSelected = new Set(selectedProducts);
      currentPageIds.forEach(id => newSelected.delete(id));
      setSelectedProducts(newSelected);
    } else {
      const newSelected = new Set(selectedProducts);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedProducts(newSelected);
    }
    selectionAnchorRef.current = null;
  }, [paginatedProducts, selectedProducts]);

  const clearSelection = useCallback(() => {
    setSelectedProducts(new Set());
    selectionAnchorRef.current = null;
  }, []);

  // Cross-page "select all N": every row the current filters match, not just
  // the visible page. Offered from the floating bar only when the page-level
  // header checkbox cannot express it.
  const handleSelectAllFiltered = useCallback(() => {
    setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    selectionAnchorRef.current = null;
  }, [filteredProducts]);

  const handleBulkAction = (actionType: "stock" | "image" | "delete") => {
    if (selectedProducts.size === 0) return;

    setBulkActionType(actionType);
    setIsBulkModalOpen(true);
  };

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'stockLevel' || field === 'velocity' ? 'desc' : 'asc');
    }
  };

  const handleDownloadCsv = () => {
    // Export selected products if any are checked, otherwise export all filtered products
    if (selectedProducts.size > 0) {
      downloadStockCsv(stocktakeSelectedRows);
    } else {
      downloadStockCsv(filteredProducts);
    }
  };

  const handleExportCountSheetSelected = useCallback(() => {
    exportRows(stocktakeSelectedRows, 'selected');
  }, [exportRows, stocktakeSelectedRows]);

  const handleExportCountSheetAll = useCallback(() => {
    exportRows(filteredProducts, 'all');
  }, [exportRows, filteredProducts]);

  const handleBulkThresholdUpload = () => {
    setIsBulkThresholdUploadOpen(true);
  };

  const handleIndividualStocktake = () => {
    setIsIndividualStocktakeModalOpen(true);
  };

  const handleBulkStocktake = () => {
    setIsBulkStocktakeUploadOpen(true);
  };

  const handleMoveToUnavailable = () => {
    setUnavailableMode('to_unavailable');
    setIsUnavailableModalOpen(true);
  };

  const handleMoveToAvailable = () => {
    setUnavailableMode('to_available');
    setIsUnavailableModalOpen(true);
  };

  // BLU-3xx Phase 47.01.2-B-04: forward optional `targetOrganizationId` to
  // the unavailable RPCs. The router schema (B-04) accepts it; the service
  // layer + RPC migration (B-01 + ISS-013) wire it as `p_actor_organization_id`.
  // BLU-3xx Phase 47.01.2-B-fix3.1: takes the resolved Product (not sku
  // string) so cross-org targets work end-to-end.
  const handleUnavailableSubmit = async (params: {
    product: Product;
    quantity: number;
    reason?: string;
    targetOrganizationId?: string;
  }) => {
    if (unavailableMode === 'to_unavailable') {
      return moveToUnavailable(
        params.product,
        params.quantity,
        params.reason,
        params.targetOrganizationId ?? null,
      );
    }
    return moveToAvailable(
      params.product,
      params.quantity,
      params.reason,
      params.targetOrganizationId ?? null,
    );
  };

  // BLU-3xx Phase 47.01.2-B-fix3.4: cross-org client filter lives in the
  // global TopHeader slot (next to the sidebar toggle). Anchored to the
  // app chrome rather than the page body. The memo keeps the registered
  // node stable across re-renders that don't change filter state.
  const headerLeftSlot = useMemo(
    () =>
      clientFilter.showClientFilter ? (
        <ClientFilterDropdown
          value={clientFilter.clientOrgFilter}
          onChange={clientFilter.setClientOrgFilter}
          ownOrgId={ownOrgId}
          orgIdToName={clientFilter.orgIdToName}
          distinctOrgsInData={clientFilter.distinctOrgsInData}
        />
      ) : null,
    [
      clientFilter.showClientFilter,
      clientFilter.clientOrgFilter,
      clientFilter.setClientOrgFilter,
      clientFilter.orgIdToName,
      clientFilter.distinctOrgsInData,
      ownOrgId,
    ],
  );
  useHeaderLeftSlot(headerLeftSlot);

  return (
    <div className="space-y-2">
      {/* Filters live inside the sticky page action bar so search, view mode,
          and bulk actions stay reachable as the operator scrolls long lists. */}
      <PageActionBar ariaLabel="Inventory actions">
        <InventoryTableFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          stockFilter={stockFilter}
          setStockFilter={setStockFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          showStockAge={showStockAge}
          setShowStockAge={setShowStockAge}
          stockAgeLoading={stockAgeLoading}
          handleDownloadCsv={handleDownloadCsv}
          onAddStockIndividualClick={onAddStockIndividual || (() => onAddStock())}
          onAddStockCsvClick={onAddStockCsv || (() => onAddStock())}
          onStocktakeIndividualClick={handleIndividualStocktake}
          onStocktakeCsvClick={handleBulkStocktake}
          onBulkThresholdCsvClick={handleBulkThresholdUpload}
          selectedCount={selectedProducts.size}
          selectedProductIds={Array.from(selectedProducts)}
          onBulkAddStock={() => handleBulkAction('stock')}
          onSetStockSelected={() => {
            if (selectedProducts.size !== 1) return;
            const selectedId = Array.from(selectedProducts)[0];
            const prod = products.find(p => p.id === selectedId);
            if (prod) {
              setPresetStocktakeSku(prod.sku);
              setIsIndividualStocktakeModalOpen(true);
            }
          }}
          onClearSelection={clearSelection}
          uniqueTypes={uniqueTypes}
          uniqueColors={uniqueColors}
          uniqueSizes={uniqueSizes}
          isFilterActive={isFilterActive}
          handleClearFilters={handleClearFilters}
          onMoveToUnavailable={handleMoveToUnavailable}
          onMoveToAvailable={handleMoveToAvailable}
          onAddPrePrintedStockClick={() => setIsPrePrintedStockModalOpen(true)}
          onReconcile={onReconcile}
          pushViewedOrganizationId={
            clientFilter.clientOrgFilter === ALL_CLIENTS
              ? null
              : clientFilter.clientOrgFilter
          }
          pushViewedOrganizationName={clientFilter.selectedClientName}
          pushHasMixedOrganizationRows={
            clientFilter.clientOrgFilter === ALL_CLIENTS &&
            clientFilter.distinctOrgsInData.some((orgId) => orgId !== ownOrgId)
          }
          stockActionsDisabled={stockActionsDisabled}
          prePrintActionsDisabled={prePrintActionsDisabled}
          prePrintActionsDisabledReason={prePrintActionsDisabledReason}
          stockActionsDisabledReason={stockActionsDisabledReason}
          countSheetAll={countSheetAll}
          productionTypeFilter={productionTypeFilter}
          onProductionTypeFilterChange={onProductionTypeFilterChange}
          whoFilter={whoFilter}
          onWhoFilterChange={onWhoFilterChange}
          whoClients={whoClients}
          onExportCountSheetSelected={handleExportCountSheetSelected}
          onExportCountSheetAll={handleExportCountSheetAll}
          onExportCountSheetExample={exportExample}
          includeCurrentCounts={includeCurrentCounts}
          onToggleIncludeCurrentCounts={setIncludeCurrentCounts}
          stocktakeSelectedCount={stocktakeSelectedRows.length}
          countSheetSelected={countSheetSelected}
          hasSelectedLocation={!!selectedLocation}
        />
      </PageActionBar>

      {/* Stock Age Callout - shown when Age is enabled in table view */}
      {viewMode === 'table' && showStockAge && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <div className="flex items-start space-x-2">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            </div>
            <div className="text-sm text-blue-800">
              Predicted days of stock remaining based on a 12-week rolling average of sales.
            </div>
          </div>
        </div>
      )}

      {/* Removed secondary actions row to reduce duplication. All actions live in the toolbar above. */}

      {/* BLU-3xx Phase 47.01.2-B-03: empty-state when the per-client filter
          narrows to zero rows. Without this, a loud purple banner over an
          empty table reads as "Mini Zali stock is gone" — alarming. The
          inline empty state explains nothing-found at this location and
          offers a one-click reset. */}
      {clientFilter.isFiltered && filteredProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-card border rounded-lg space-y-3">
          {/* BLU-3xx Phase 47.01.2-B-fix HIGH-7: visual anchor for the
              filtered-empty state. Subtle muted icon at the top —
              matches the EmptyState pattern used elsewhere in the
              inventory app. */}
          <PackageOpen
            className="h-10 w-10 text-muted-foreground/40 mb-1"
            aria-hidden="true"
          />
          {/* BLU-3xx Phase 47.01.2-B-fix2 NEW-FH1.1: i18n the
              empty-state copy. Uses <Trans> to render the embedded
              <strong> wrapper around the client name. The location-
              less variant of the title key handles "All Locations"
              header mode. */}
          <p className="text-base text-foreground">
            {selectedLocation ? (
              <Trans
                t={t}
                i18nKey="crossOrg.emptyState.title"
                values={{
                  client: clientFilter.selectedClientName ?? '',
                  location: selectedLocation.name,
                }}
                components={{ strong: <span className="font-medium" /> }}
              />
            ) : (
              <Trans
                t={t}
                i18nKey="crossOrg.emptyState.titleNoLocation"
                values={{ client: clientFilter.selectedClientName ?? '' }}
                components={{ strong: <span className="font-medium" /> }}
              />
            )}
          </p>
          <p className="text-sm text-muted-foreground max-w-md">
            {t('crossOrg.emptyState.subtitle')}
          </p>
          {/* BLU-3xx Phase 47.01.2-B-fix2 NEW-FM4: variant=outline
              matches `ReceiveHistory.tsx`'s filtered-empty CTA — both
              surfaces now expose the same affordance hierarchy for
              "reset filter" actions. Pre-fix used variant=ghost which
              was visually quieter than the page's primary
              "intentional empty state" affordance. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => clientFilter.setClientOrgFilter(ALL_CLIENTS)}
          >
            {t('crossOrg.banner.viewAll')}
          </Button>
        </div>
      ) : (
        <>
          {/* List View */}
          {viewMode === 'list' && (
            <InventoryTableList
              products={paginatedProducts}
              loading={loading}
              selectedProducts={selectedProducts}
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
              onCheckboxChange={handleCheckboxChange}
              onSelectAll={handleSelectAll}
              onClearSelection={clearSelection}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              onRowClick={(product) => setSelectedProductForDetails(product)}
              velocityBySkuId={velocityBySkuId}
              velocityLoading={velocityQuery.isLoading}
              velocityUnavailable={velocityQuery.isError}
              velocityRangeDays={velocityQuery.data?.rangeDays ?? 30}
              maxVelocity={maxVelocity}
              showOwnerColumn={clientFilter.showClientFilter && !clientFilter.isFiltered}
              ownOrgId={ownOrgId}
              ownOrgName={organization?.name}
              orgIdToName={clientFilter.orgIdToName}
              reservedClientName={reservedClientName}
            />
          )}

          {/* Matrix View */}
          {viewMode === 'table' && (
            <InventoryTableMatrix
              filteredProducts={displayProducts}
              showStockAge={showStockAge}
              stockAgeMap={stockAgeMap}
              showOwnerBadge={clientFilter.showClientFilter && !clientFilter.isFiltered}
              ownOrgId={ownOrgId}
              ownOrgName={organization?.name}
              orgIdToName={clientFilter.orgIdToName}
              loading={loading}
            />
          )}
        </>
      )}

      {/* Modals - only shown in list view */}
      {viewMode === 'list' && (
        <>
          <BulkActionModal
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            actionType={bulkActionType}
            selectedProductIds={Array.from(selectedProducts)}
            products={stocktakeSelectedRows}
            onSuccess={() => {
              setIsBulkModalOpen(false);
              setSelectedProducts(new Set());
              onProductUpdated();
            }}
          />

          {onSetStock && (
            <IndividualStocktakeModal
              isOpen={isIndividualStocktakeModalOpen}
              onClose={() => { setIsIndividualStocktakeModalOpen(false); setPresetStocktakeSku(null); }}
              onSetStock={onSetStock}
              products={products}
              defaultSku={presetStocktakeSku || undefined}
            />
          )}

          <BulkStocktakeUploadModal
            isOpen={isBulkStocktakeUploadOpen}
            onClose={() => setIsBulkStocktakeUploadOpen(false)}
            onSuccess={onProductUpdated}
          />

          <BulkThresholdUploadModal
            isOpen={isBulkThresholdUploadOpen}
            onClose={() => setIsBulkThresholdUploadOpen(false)}
            onSuccess={onProductUpdated}
          />
        </>
      )}

      <UnavailableAdjustmentModal
        isOpen={isUnavailableModalOpen}
        onClose={() => setIsUnavailableModalOpen(false)}
        products={products}
        direction={unavailableMode}
        onSubmit={handleUnavailableSubmit}
      />

      <AddPrePrintedStockModal
        isOpen={isPrePrintedStockModalOpen}
        onClose={() => setIsPrePrintedStockModalOpen(false)}
      />

      <InventoryProductDetailModal
        open={selectedProductForDetails !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedProductForDetails(null);
        }}
        product={selectedProductForDetails}
        onSetStock={onSetStock}
      />

      {/* Floating selection chrome — list view only (the matrix view has no
          row selection). Kept MOUNTED so the shared primitive can play its
          exit animation when the last row is deselected. */}
      {viewMode === 'list' && (
        <>
          <InventoryBulkSelectionBar
            selectedCount={stocktakeSelectedRows.length}
            filteredCount={filteredProducts.length}
            onSelectAllFiltered={handleSelectAllFiltered}
            onClearSelection={clearSelection}
            onEditStock={() => setIsBulkStockEditorOpen(true)}
            editStockDisabled={stockActionsDisabled}
            editStockDisabledReason={stockActionsDisabledReason}
          />

          <BulkStockEditorDialog
            open={isBulkStockEditorOpen}
            onOpenChange={setIsBulkStockEditorOpen}
            products={stocktakeSelectedRows}
            ownOrgId={ownOrgId}
            orgIdToName={clientFilter.orgIdToName}
            onSuccess={() => {
              setIsBulkStockEditorOpen(false);
              clearSelection();
              onProductUpdated();
            }}
          />
        </>
      )}
    </div>
  );
};

export default InventoryTable;
